/**
 * Molecule: MobileProjectSwitcher
 * Mobile project switcher — tap the pill to open a bottom sheet listing projects
 * Uses ProjectContext for persistence; bottom sheet style matches MobileNav More drawer
 */
import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { FolderKanban, Globe, ChevronDown, Search, X, Pin } from 'lucide-react'
import { useProject, ActiveProject } from '@/contexts/ProjectContext'
import { useProjects } from '@/contexts/ProjectsContext'
import { tokens } from '@/design-system/tokens'
import { surfaceRouteForScope } from '@/lib/routes'
import { useProjectPins } from '@/hooks/useProjectPins'

interface Project {
  id: string | number
  name: string
  status: string
}

const STATUS_DOT: Record<string, string> = {
  active:    'var(--st-active)',
  completed: 'var(--st-done)',
  on_hold:   'var(--st-onhold)',
  cancelled: 'var(--st-blocked)',
  draft:     'var(--st-draft)',
}

function statusColor(status: string): string {
  return STATUS_DOT[status] ?? 'var(--st-draft)'
}

export function MobileProjectSwitcher() {
  const { activeProject, setActiveProject, setViewAll } = useProject()
  const { projects: ctxProjects, loading } = useProjects()
  const navigate = useNavigate()
  const location = useLocation()
  const projects: Project[] = ctxProjects.map(p => ({
    id: p.id as string | number,
    name: p.name,
    status: (p as any).status || 'active',
  }))
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  const { isPinned, togglePin } = useProjectPins()

  // Reset search on close
  useEffect(() => {
    if (!open) setSearch('')
  }, [open])

  const filtered = projects
    .filter(p => p.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => Number(isPinned(b.id)) - Number(isPinned(a.id)))
  const pinnedFilteredCount = filtered.filter(p => isPinned(p.id)).length

  function selectProject(p: Project | null) {
    if (p) {
      setActiveProject({ id: p.id, name: p.name, status: p.status })
      setViewAll(false)
    } else {
      setActiveProject(null)
      setViewAll(true)
    }
    setOpen(false)
    // Navigate to the equivalent surface at the new scope
    const target = surfaceRouteForScope(location.pathname, p?.id)
    if (target) navigate(target)
  }

  return (
    <>
      {/* Trigger pill */}
      <button
        onClick={() => setOpen(true)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          minHeight: 44,
          padding: '0 12px',
          background: tokens.color.background.page,
          border: `1px solid ${tokens.color.border.default}`,
          borderRadius: 22,
          cursor: 'pointer',
          color: tokens.color.text.primary,
          fontSize: 13,
          fontWeight: 500,
          fontFamily: tokens.typography.fontFamily.sans,
          maxWidth: '100%',
          overflow: 'hidden',
          transition: 'border-color 120ms ease, background 120ms ease',
        }}
      >
        {activeProject ? (
          <FolderKanban size={16} color={tokens.color.brand?.gold ?? 'var(--brand-gold)'} />
        ) : (
          <Globe size={16} color={tokens.color.text.secondary} />
        )}
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {activeProject ? activeProject.name : 'All Projects'}
        </span>
        <ChevronDown size={14} color={tokens.color.text.secondary} />
      </button>

      {/* Backdrop */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.55)',
            // Above the bottom nav so the whole screen dims behind the sheet.
            zIndex: 1200,
          }}
        />
      )}

      {/* Bottom sheet */}
      <div
        style={{
          position: 'fixed',
          // Sit the sheet ABOVE the bottom nav (height ~78px + safe area) so the
          // list is never clipped by it and a single-project list is fully
          // visible (Joe: "hidden by bottom nav a bit" + single project
          // compresses). Floating above the nav is stacking-context-proof.
          bottom: 'calc(78px + env(safe-area-inset-bottom))',
          left: 0,
          right: 0,
          zIndex: 1201,
          borderRadius: '22px 22px 0 0',
          background: tokens.color.background.elevated,
          maxHeight: '60vh',
          minHeight: 180,
          display: 'flex',
          flexDirection: 'column',
          transform: open ? 'translateY(0)' : 'translateY(120%)',
          transition: 'transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* Drag handle */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px', flexShrink: 0 }}>
          <div style={{ width: 36, height: 5, borderRadius: 3, background: 'var(--border-strong)' }} />
        </div>
        {/* Sheet header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 16px 12px',
          borderBottom: `1px solid ${tokens.color.border.default}`,
          flexShrink: 0,
        }}>
          <span style={{
            fontSize: 14,
            fontWeight: 600,
            color: tokens.color.text.primary,
            fontFamily: tokens.typography.fontFamily.sans,
            letterSpacing: '-0.005em',
          }}>
            Switch Project
          </span>
          <button
            onClick={() => setOpen(false)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              color: tokens.color.text.secondary,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: '12px 16px', flexShrink: 0, borderBottom: `1px solid ${tokens.color.border.default}` }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: tokens.color.background.page,
            border: `1px solid ${tokens.color.border.default}`,
            borderRadius: 8,
            padding: '8px 12px',
          }}>
            <Search size={16} color={tokens.color.text.secondary} />
            <input
              ref={searchRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search projects…"
              style={{
                flex: 1,
                background: 'none',
                border: 'none',
                outline: 'none',
                fontSize: 13,
                color: tokens.color.text.primary,
                fontFamily: tokens.typography.fontFamily.sans,
              }}
            />
          </div>
        </div>

        {/* Project list */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {/* All Projects row */}
          <button
            onClick={() => selectProject(null)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              width: '100%',
              padding: '14px 16px',
              background: !activeProject ? 'var(--brand-gold-quiet)' : 'none',
              border: 'none',
              borderBottom: `1px solid ${tokens.color.border.default}`,
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <Globe size={18} color={tokens.color.text.secondary} />
            <span style={{
              fontSize: 14,
              fontWeight: !activeProject ? 600 : 400,
              color: !activeProject ? (tokens.color.brand?.gold ?? 'var(--brand-gold)') : tokens.color.text.primary,
              fontFamily: tokens.typography.fontFamily.sans,
            }}>
              All Projects
            </span>
          </button>

          {/* Project rows */}
          {loading && (
            <div style={{ padding: 24, textAlign: 'center', color: tokens.color.text.secondary, fontSize: 13, fontFamily: tokens.typography.fontFamily.sans }}>
              Loading…
            </div>
          )}
          {!loading && pinnedFilteredCount > 0 && (
            <div style={{
              padding: '10px 16px 4px', fontSize: 11, fontWeight: 700,
              letterSpacing: '0.08em', textTransform: 'uppercase' as const,
              color: tokens.color.text.muted, fontFamily: tokens.typography.fontFamily.sans,
            }}>
              Pinned
            </div>
          )}
          {!loading && filtered.map((p, idx) => {
            const isActive = activeProject?.id === p.id
            const pinned = isPinned(p.id)
            return (
              <React.Fragment key={p.id}>
                {idx === pinnedFilteredCount && pinnedFilteredCount > 0 && (
                  <div style={{ borderTop: `1px solid ${tokens.color.border.default}` }} />
                )}
                <button
                  onClick={() => selectProject(p)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    width: '100%',
                    padding: '14px 16px',
                    background: isActive ? 'var(--brand-gold-quiet)' : 'none',
                    border: 'none',
                    borderBottom: `1px solid ${tokens.color.border.default}`,
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  {/* Status dot */}
                  <span style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: statusColor(p.status),
                    flexShrink: 0,
                  }} />
                  <span style={{
                    fontSize: 14,
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? (tokens.color.brand?.gold ?? 'var(--brand-gold)') : tokens.color.text.primary,
                    fontFamily: tokens.typography.fontFamily.sans,
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {p.name}
                  </span>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={e => { e.stopPropagation(); togglePin(p.id) }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); togglePin(p.id) }
                    }}
                    aria-label={pinned ? 'Unpin project' : 'Pin project'}
                    style={{ flexShrink: 0, display: 'flex', alignItems: 'center', padding: 4 }}
                  >
                    <Pin size={16} color={pinned ? (tokens.color.brand?.gold ?? 'var(--brand-gold)') : tokens.color.text.secondary} style={pinned ? { fill: tokens.color.brand?.gold ?? 'var(--brand-gold)' } : {}} />
                  </span>
                </button>
              </React.Fragment>
            )
          })}
          {!loading && filtered.length === 0 && search && (
            <div style={{ padding: 24, textAlign: 'center', color: tokens.color.text.secondary, fontSize: 13, fontFamily: tokens.typography.fontFamily.sans }}>
              No projects match "{search}"
            </div>
          )}
        </div>
      </div>
    </>
  )
}
